import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { School } from '@gt/dropdown-school/org.school';
import { Area } from '@gt/dropdown-area/org.area';
import { District } from '@gt/dropdown-district/org.district';
import { NodeTypeId, nodeHasTypeId } from '@para/shared/para-node-type.util';
import type { ParaClassroom } from '@para/shared/models/para-classroom.model';
import type { ParaTreeNodeModel, ParaTreeRootNode } from '@para/shared/para-tree-node.types';

const PARA_SELECTED_NODE_ID_KEY = 'paraSelectedNodeId';

export interface ParaDistrict extends District {
	districtId?: number | string;
}

export type ParaSelectedNode =
	| { kind: 'school'; node: School }
	| { kind: 'classroom'; node: ParaClassroom }
	| { kind: 'area'; node: Area }
	| { kind: 'district'; node: ParaDistrict };

/** Org node id used for tree + report APIs. For districts, prefer `districtId` (JWT can put a session id in `id`). */
export function paraSelectedNodeTreeId(sel: ParaSelectedNode): number | string | null {
	if (sel.kind === 'district' && sel.node.districtId != null) {
		return sel.node.districtId;
	}
	return sel.node.id ?? null;
}

function shallow<T>(obj: T): T {
	return obj == null ? obj : ({ ...(obj as unknown as object) } as T);
}

function modelOf(root: ParaTreeRootNode): ParaTreeNodeModel | null {
	return root.model ?? ((root as { id?: unknown }).id != null ? (root as unknown as ParaTreeNodeModel) : null);
}

function preorderModels(root: ParaTreeRootNode, out: ParaTreeNodeModel[] = []): ParaTreeNodeModel[] {
	const m = modelOf(root);
	if (m?.id != null) {
		out.push(m);
	}
	for (const c of root.children ?? []) {
		preorderModels(c, out);
	}
	return out;
}

function persistSelectedId(id: number | string | null): void {
	if (id == null || id === '') {
		return;
	}
	try {
		localStorage.setItem(PARA_SELECTED_NODE_ID_KEY, String(id));
	} catch {
		/* ignore quota / private mode / SSR */
	}
}

function readPersistedSelectedId(): string | null {
	try {
		return localStorage.getItem(PARA_SELECTED_NODE_ID_KEY);
	} catch {
		return null;
	}
}

@Injectable({ providedIn: 'root' })
export class ParaOrgStateService {
	private readonly selectedNodeSubject = new BehaviorSubject<ParaSelectedNode | null>(null);
	private readonly scopeInitializingSubject = new BehaviorSubject<boolean>(true);
	private modelById = new Map<string, ParaTreeNodeModel>();

	/** True while the org tree and initial scope selection are still loading. */
	public readonly scopeInitializing$ = this.scopeInitializingSubject.asObservable();

	/** PARA source of truth: selected tree scope. Late: emits only once a selection exists. */
	public readonly selectedParaNode$: Observable<ParaSelectedNode> = this.selectedNodeSubject.asObservable().pipe(
		filter((x): x is ParaSelectedNode => x != null),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Raw entity for `[node]` inputs. For districts, `id` is forced to the org node id. */
	public readonly selectedParaNodeEntity$: Observable<School | ParaClassroom | Area | District> = this.selectedParaNode$.pipe(
		map((x) => {
			const n = shallow(x.node);
			if (x.kind === 'district' && (n as ParaDistrict).districtId != null) {
				(n as ParaDistrict).id = (n as ParaDistrict).districtId as number;
			}
			return n;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	public readonly selectedSchool$: Observable<School> = this.selectedParaNode$.pipe(
		filter((x): x is { kind: 'school'; node: School } => x.kind === 'school'),
		map((x) => shallow(x.node)),
		distinctUntilChanged((a, b) => a?.id === b?.id),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	public readonly selectedClassroom$: Observable<ParaClassroom> = this.selectedParaNode$.pipe(
		filter((x): x is { kind: 'classroom'; node: ParaClassroom } => x.kind === 'classroom'),
		map((x) => shallow(x.node)),
		distinctUntilChanged((a, b) => a?.id === b?.id),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	/** Index nodes by id from the tree root for selection resolution. */
	setOrgTreeRoot(root: ParaTreeRootNode | null): void {
		this.modelById = new Map(root ? preorderModels(root).map((m) => [String(m.id), m]) : []);
	}

	setScopeInitializing(initializing: boolean): void {
		this.scopeInitializingSubject.next(!!initializing);
	}

	/** Classroom scope when the selected node is a class; null otherwise (sync snapshot for drawers). */
	getSelectedClassroomSnapshot(): ParaClassroom | null {
		const v = this.selectedNodeSubject.value;
		return v?.kind === 'classroom' ? v.node : null;
	}

	/** Tree/report node id for the current PARA scope (sync snapshot). */
	getSelectedScopeNodeId(): number | string | null {
		const v = this.selectedNodeSubject.value;
		return v ? paraSelectedNodeTreeId(v) : null;
	}

	/** Select PARA scope from a `getTreeView` node. Uses the index from {@link setOrgTreeRoot}. */
	public selectFromTreeNode(treeNodeModel: unknown): void {
		if (!treeNodeModel || typeof treeNodeModel !== 'object') {
			return;
		}
		const sel = this.resolveSelection(treeNodeModel as ParaTreeNodeModel);
		if (sel) {
			persistSelectedId(paraSelectedNodeTreeId(sel));
			this.selectedNodeSubject.next(sel);
		}
	}

	/**
	 * After the tree loads: try the saved id, then a single-node root, then the first
	 * classroom / school / area / district in pre-order.
	 */
	public ensureInitialScope(root: ParaTreeRootNode | null, options?: { singleNodeRootModel?: ParaTreeNodeModel | null }): void {
		if (this.selectedNodeSubject.value != null || !root) {
			return;
		}

		const saved = readPersistedSelectedId();
		const candidates: Array<ParaTreeNodeModel | null | undefined> = [
			saved ? this.modelById.get(saved) : null,
			options?.singleNodeRootModel,
			this.firstScopeModelInPreorder(root),
		];

		for (const c of candidates) {
			if (!c) {
				continue;
			}
			this.selectFromTreeNode(c);
			if (this.selectedNodeSubject.value != null) {
				return;
			}
		}
	}

	/** Map a tree node to a {@link ParaSelectedNode}. Tree shape: district → area? → school → classroom. */
	private resolveSelection(m: ParaTreeNodeModel): ParaSelectedNode | null {
		const id = Number(m.id);
		const name = String(m.name ?? '');

		if (nodeHasTypeId(m, NodeTypeId.NT_DISTRICT)) {
			return { kind: 'district', node: { id, name, district: id, districtId: id } as ParaDistrict };
		}
		if (nodeHasTypeId(m, NodeTypeId.NT_AREA)) {
			return { kind: 'area', node: { id, name, district: Number(m.parentId) } as Area };
		}
		if (nodeHasTypeId(m, NodeTypeId.NT_SCHOOL)) {
			const parent = this.modelById.get(String(m.parentId));
			const parentIsArea = !!parent && nodeHasTypeId(parent, NodeTypeId.NT_AREA);
			const area = parentIsArea ? Number(parent!.id) : 0;
			const district = parentIsArea ? Number(parent!.parentId) : Number(m.parentId);
			return { kind: 'school', node: { id, name, area, district } as School };
		}
		if (nodeHasTypeId(m, NodeTypeId.NT_CLASS)) {
			const assessmentBookId = (m as { assessmentBookId?: unknown }).assessmentBookId ?? null;
			return {
				kind: 'classroom',
				node: { id, name, schoolId: Number(m.parentId), assessmentBookId } as ParaClassroom,
			};
		}
		return null;
	}

	private firstScopeModelInPreorder(root: ParaTreeRootNode): ParaTreeNodeModel | null {
		const list = preorderModels(root);
		const find = (t: number) => list.find((x) => nodeHasTypeId(x, t));
		return find(NodeTypeId.NT_CLASS) ?? find(NodeTypeId.NT_SCHOOL) ?? find(NodeTypeId.NT_AREA) ?? find(NodeTypeId.NT_DISTRICT) ?? null;
	}
}
